import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import SEOHead from "../../common/SEOHead";

import "./Services.css";
import { baseUrl } from "../../config";
import { trackEvent } from "../../components/trackingUtils";
import TemplateDetails from "../TemplateDetails";

const Services = ({ services }) => {
  let { url } = useParams();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const trackPageLoad = async () => {
      try {
        await trackEvent("PageLoad", url || "", "Services");
      } catch (error) {
        console.error("Error tracking page load:", error);
      } finally {
        setIsLoading(false);
      }
    };

    trackPageLoad();
  }, [url]);

  // Scroll progress indicator
  useEffect(() => {
    const updateScrollProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min(scrollTop / docHeight, 1) * 100;
      setScrollProgress(progress);
    };

    const handleScroll = () => {
      updateScrollProgress();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const service = services.find((service) => service.url === url);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center px-4 max-w-lg mx-auto">
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#E5C558]/20 to-[#E5C558]/40 flex items-center justify-center">
              <svg
                className="w-12 h-12 text-[#E5C558]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.137 0-4.146-.832-5.657-2.343"
                />
              </svg>
            </div>
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4 tracking-tight">
            Service Not Found
          </h1>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            The service you're looking for doesn't exist or may have been moved.
          </p>
          <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
            <Link
              to="/services"
              className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-[#0b1220] rounded-full font-semibold hover:shadow-xl hover:shadow-[#E5C558]/50 transition-all hover:scale-105 w-full sm:w-auto"
              aria-label="View all available services"
            >
              View All Services
              <svg
                className="w-5 h-5 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-gray-100 hover:border-gray-400 transition-all w-full sm:w-auto"
              aria-label="Go back to homepage"
            >
              Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (url === "website-building") {
    return (
      <>
        <SEOHead
          title={`${service.title} - Professional Website Building Services | GeniusNexa`}
          description={`${service.description} Get custom website development, responsive design, and SEO optimization. Transform your business with our professional web solutions.`}
          keywords={service.keywords}
          image={service.imagefullurl}
          url={`${baseUrl}/services/${service.url}`}
          type="service"
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: service.title,
            description: service.description,
            provider: {
              "@type": "Organization",
              name: "GeniusNexa",
              url: baseUrl,
            },
            url: `${baseUrl}/services/${service.url}`,
            image: service.imagefullurl,
            serviceType: "Website Development",
            areaServed: "Global",
          })}
        </script>
        <TemplateDetails />
      </>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#E5C558] mb-4"></div>
          <p className="text-gray-600">Loading service details...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* SEO and Structured Data */}
      <SEOHead
        title={`${service.title} - Professional ${service.title} Services | GeniusNexa`}
        description={`${
          service.description
        } Get expert ${service.title.toLowerCase()} services with industry-leading solutions, 24/7 support, and proven results. Contact us for a free consultation.`}
        keywords={service.keywords}
        image={service.imagefullurl}
        url={`${baseUrl}/services/${service.url}`}
        type="service"
        section="Services"
      />

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: service.title,
          description: service.description,
          provider: {
            "@type": "Organization",
            name: "GeniusNexa",
            url: baseUrl,
            logo: `${baseUrl}/images/logo.png`,
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+91 9944544603",
              contactType: "Customer Service",
              availableLanguage: "English",
            },
          },
          url: `${baseUrl}/services/${service.url}`,
          image: service.imagefullurl,
          serviceType: service.title,
          areaServed: "Global",
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: service.title,
            itemListElement:
              service.details?.map((detail, index) => ({
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: detail.title,
                  description: detail.description
                    .replace(/<[^>]*>/g, "")
                    .substring(0, 200),
                },
              })) || [],
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.9",
            bestRating: "5",
            ratingCount: "150",
          },
        })}
      </script>

      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <div
          className="h-1 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] transition-all duration-150"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Hero Section */}
        <section
          className="relative pt-20 md:pt-0 min-h-[420px] md:min-h-[500px] lg:min-h-[600px] overflow-visible"
          data-section="hero"
        >
          {/* Background Image with Enhanced Overlay */}
          <div className="absolute inset-0">
            <img
              src={service.imagefullurl}
              alt={`${service.title} hero background`}
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0b1220]/98 via-[#0b1220]/90 to-[#0b1220]/75"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b1220]/90 via-[#0b1220]/20 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900/30 via-transparent to-purple-900/20"></div>
          </div>

          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-40 h-40 bg-gradient-to-br from-[#E5C558]/20 to-yellow-400/10 rounded-full animate-pulse"></div>
            <div className="absolute top-3/4 right-1/4 w-32 h-32 bg-gradient-to-br from-blue-400/15 to-[#E5C558]/10 rounded-full animate-pulse delay-1000"></div>
            <div className="absolute bottom-1/4 left-1/3 w-28 h-28 bg-gradient-to-br from-purple-400/10 to-[#E5C558]/10 rounded-full animate-pulse delay-2000"></div>
            <div className="absolute top-1/2 right-1/3 w-24 h-24 bg-gradient-to-br from-[#E5C558]/15 to-orange-400/10 rounded-full animate-pulse delay-3000"></div>
          </div>

          {/* Hero Content */}
          <div className="relative z-10 container mx-auto px-4 lg:px-6 h-full flex items-center">
            <div className="max-w-4xl">
              <div className="space-y-6 animate-fadeInUp">
                <div className="inline-flex items-center px-6 py-3 bg-[#E5C558]/20 backdrop-blur-sm rounded-full text-[#E5C558] font-semibold mb-6 border border-[#E5C558]/30">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  Premium {service.title} Services
                </div>

                <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-7xl font-bold text-white mb-4 leading-tight tracking-tight">
                  {service.title}
                  <span className="block text-[#E5C558] text-lg sm:text-xl md:text-3xl lg:text-4xl font-normal mt-1">
                    Excellence Delivered
                  </span>
                </h1>

                <p className="text-base sm:text-lg lg:text-2xl text-gray-200 mb-6 leading-relaxed max-w-3xl">
                  {service.description} Our expert team delivers
                  industry-leading solutions with proven results and 24/7
                  support.
                </p>

                {/* Key Benefits */}
                <div className="flex flex-wrap gap-4 mb-8">
                  <div className="flex items-center text-gray-200">
                    <svg
                      className="w-5 h-5 mr-2 text-[#E5C558]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Industry Experts
                  </div>
                  <div className="flex items-center text-gray-200">
                    <svg
                      className="w-5 h-5 mr-2 text-[#E5C558]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    24/7 Support
                  </div>
                  <div className="flex items-center text-gray-200">
                    <svg
                      className="w-5 h-5 mr-2 text-[#E5C558]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Proven Results
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <a
                    href="#details"
                    className="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-[#0b1220] font-semibold rounded-full hover:shadow-xl hover:shadow-[#E5C558]/50 transition-all duration-300 hover:scale-105 focus:ring-4 focus:ring-[#E5C558]/30"
                    onClick={() =>
                      trackEvent(
                        "CTA",
                        `LearnMore_${service.url}`,
                        "ServicePage"
                      )
                    }
                    aria-label={`Learn more about ${service.title} services`}
                  >
                    Explore Our Solutions
                    <svg
                      className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                      />
                    </svg>
                  </a>

                  <Link
                    to="/contact-us"
                    className="group inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-[#0b1220] transition-all duration-300 focus:ring-4 focus:ring-white/30"
                    onClick={() =>
                      trackEvent("CTA", `Contact_${service.url}`, "ServicePage")
                    }
                    aria-label={`Get started with ${service.title} services`}
                  >
                    Get Free Consultation
                    <svg
                      className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </div>
        </section>

        {/* Service Overview Cards */}
        <section
          className="py-20 lg:py-28 bg-gradient-to-br from-slate-50 via-white to-slate-100 relative"
          data-section="overview"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <defs>
                <pattern
                  id="dots"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <circle cx="10" cy="10" r="1" fill="#E5C558" />
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#dots)" />
            </svg>
          </div>

          <div className="container mx-auto px-4 lg:px-6 relative z-10">
            <div className="text-center mb-20">
              <div className="inline-block px-6 py-3 bg-gradient-to-r from-[#E5C558]/20 to-yellow-400/20 backdrop-blur-sm rounded-full text-[#E5C558] font-semibold mb-6 border border-[#E5C558]/30">
                Our Advantages
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Why Choose Our {service.title}?
              </h2>
              <p className="text-base sm:text-lg lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                We deliver exceptional results through innovative solutions,
                expert knowledge, and unwavering commitment to your success.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
              <div className="group">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 lg:p-10 text-center shadow-lg hover:shadow-2xl hover:shadow-[#E5C558]/20 transition-all duration-500 hover:-translate-y-2 border border-white/50">
                  <div className="w-20 h-20 mx-auto mb-8 bg-gradient-to-br from-[#E5C558] via-yellow-400 to-[#d4b54d] rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                    <svg
                      className="w-10 h-10 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 group-hover:text-[#E5C558] transition-colors">
                    Quality Assurance
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    Rigorous testing and quality control ensure every
                    deliverable meets the highest standards with zero
                    compromise.
                  </p>
                  <div className="mt-6 h-1 w-16 bg-gradient-to-r from-[#E5C558] to-yellow-400 rounded-full mx-auto group-hover:w-24 transition-all duration-500"></div>
                </div>
              </div>

              <div className="group">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 lg:p-10 text-center shadow-lg hover:shadow-2xl hover:shadow-blue-400/20 transition-all duration-500 hover:-translate-y-2 border border-white/50">
                  <div className="w-20 h-20 mx-auto mb-8 bg-gradient-to-br from-blue-500 via-blue-400 to-indigo-500 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                    <svg
                      className="w-10 h-10 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 group-hover:text-blue-600 transition-colors">
                    Rapid Delivery
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    Streamlined processes and agile methodologies ensure fast
                    turnaround times without compromising quality.
                  </p>
                  <div className="mt-6 h-1 w-16 bg-gradient-to-r from-blue-500 to-indigo-400 rounded-full mx-auto group-hover:w-24 transition-all duration-500"></div>
                </div>
              </div>

              <div className="group">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 lg:p-10 text-center shadow-lg hover:shadow-2xl hover:shadow-purple-400/20 transition-all duration-500 hover:-translate-y-2 border border-white/50">
                  <div className="w-20 h-20 mx-auto mb-8 bg-gradient-to-br from-purple-500 via-purple-400 to-pink-500 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                    <svg
                      className="w-10 h-10 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 group-hover:text-purple-600 transition-colors">
                    Expert Team
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    Our certified professionals bring years of experience and
                    industry best practices to every project.
                  </p>
                  <div className="mt-6 h-1 w-16 bg-gradient-to-r from-purple-500 to-pink-400 rounded-full mx-auto group-hover:w-24 transition-all duration-500"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Service Details */}
        <section
          id="details"
          className="py-20 lg:py-28 bg-gradient-to-b from-white via-slate-50 to-gray-50 relative"
          data-section="details"
        >
          {/* Background Decoration */}
          <div className="absolute inset-0 overflow-hidden opacity-10">
            <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-[#E5C558]/30 to-yellow-400/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-blue-400/30 to-purple-400/20 rounded-full blur-3xl"></div>
          </div>

          <div className="container mx-auto px-4 lg:px-6 relative z-10">
            <div className="text-center mb-20">
              <div className="inline-block px-6 py-3 bg-gradient-to-r from-[#E5C558]/20 to-yellow-400/20 backdrop-blur-sm rounded-full text-[#E5C558] font-semibold mb-6 border border-[#E5C558]/30">
                Service Details
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-8 leading-tight">
                Comprehensive {service.title} Solutions
              </h2>
              <p className="text-xl lg:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed font-medium">
                Explore our detailed service offerings designed to meet your
                unique business requirements and drive measurable results.
              </p>
            </div>

            <div className="space-y-16 lg:space-y-24">
              {service.details &&
                service.details.map((detail, index) => (
                  <div
                    key={index}
                    className={`group relative ${
                      index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                    } flex flex-col lg:flex gap-8 lg:gap-16 items-center`}
                  >
                    {/* Content */}
                    <div className="flex-1 space-y-6">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-[#E5C558] to-[#d4b54d] flex items-center justify-center text-[#0b1220] font-bold text-xl shadow-lg">
                          {index + 1}
                        </div>
                        <div className="h-px flex-1 bg-gradient-to-r from-[#E5C558] to-transparent"></div>
                      </div>

                      <h3 className="text-2xl lg:text-3xl font-bold text-gray-800 leading-tight">
                        {detail.title}
                      </h3>

                      <div
                        className="prose prose-lg prose-gray max-w-none text-gray-800 leading-relaxed font-medium"
                        dangerouslySetInnerHTML={{ __html: detail.description }}
                      />

                      <div className="pt-4">
                        <Link
                          to="/contact-us"
                          className="inline-flex items-center text-[#E5C558] font-semibold hover:text-[#d4b54d] transition-colors group"
                          onClick={() =>
                            trackEvent(
                              "CTA",
                              `DetailContact_${service.url}_${index}`,
                              "ServicePage"
                            )
                          }
                        >
                          Learn More About This Service
                          <svg
                            className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 7l5 5m0 0l-5 5m5-5H6"
                            />
                          </svg>
                        </Link>
                      </div>
                    </div>

                    {/* Visual Element */}
                    <div className="flex-1 lg:max-w-md">
                      <div className="relative">
                        <div className="w-full h-64 lg:h-80 bg-gradient-to-br from-[#E5C558]/20 to-[#d4b54d]/20 rounded-2xl overflow-hidden group-hover:shadow-2xl transition-shadow duration-300">
                          <div className="absolute inset-0 bg-gradient-to-br from-[#E5C558]/10 to-[#d4b54d]/10 group-hover:from-[#E5C558]/20 group-hover:to-[#d4b54d]/20 transition-colors duration-300"></div>
                          <div className="relative z-10 p-8 h-full flex items-center justify-center">
                            <div className="text-center space-y-4">
                              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-[#E5C558] to-[#d4b54d] rounded-full flex items-center justify-center">
                                <svg
                                  className="w-8 h-8 text-[#0b1220]"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                              </div>
                              <h4 className="text-lg font-semibold text-gray-800">
                                Professional Solution
                              </h4>
                              <p className="text-gray-600 text-sm">
                                Industry-leading approach with proven
                                methodologies
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section
          className="py-20 lg:py-28 bg-gradient-to-br from-slate-100 via-white to-slate-50 relative"
          data-section="testimonials"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <defs>
                <pattern
                  id="testimonial-grid"
                  width="25"
                  height="25"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 25 0 L 0 0 0 25"
                    fill="none"
                    stroke="#E5C558"
                    strokeWidth="0.5"
                  />
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#testimonial-grid)" />
            </svg>
          </div>

          <div className="container mx-auto px-4 lg:px-6 relative z-10">
            <div className="text-center mb-20">
              <div className="inline-block px-6 py-3 bg-gradient-to-r from-[#E5C558]/20 to-yellow-400/20 backdrop-blur-sm rounded-full text-[#E5C558] font-semibold mb-6 border border-[#E5C558]/30">
                Client Success Stories
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-8 leading-tight">
                What Our Clients Say
              </h2>
              <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                Don't just take our word for it. See what our satisfied clients
                have to say about our {service.title.toLowerCase()} services.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
              {[1, 2, 3].map((testimonial) => (
                <div
                  key={testimonial}
                  className="group bg-white/90 backdrop-blur-sm rounded-3xl p-8 lg:p-10 hover:shadow-2xl hover:shadow-[#E5C558]/20 transition-all duration-500 hover:-translate-y-2 border border-white/50 relative overflow-hidden"
                >
                  {/* Card Background Decoration */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-[#E5C558]/10 to-yellow-400/5 rounded-bl-3xl"></div>

                  <div className="flex items-center mb-6">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className="w-6 h-6 text-[#E5C558] group-hover:text-yellow-400 transition-colors"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>

                  <blockquote className="text-gray-700 mb-8 leading-relaxed text-lg font-medium">
                    "Excellent service delivery and professional team. They
                    exceeded our expectations and delivered exceptional results
                    on time with outstanding quality."
                  </blockquote>

                  <div className="flex items-center">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E5C558] to-[#d4b54d] rounded-2xl flex items-center justify-center text-[#0b1220] font-bold text-lg mr-4 group-hover:scale-110 transition-transform duration-300">
                      {String.fromCharCode(64 + testimonial)}
                    </div>
                    <div>
                      <div className="font-bold text-gray-900 text-lg group-hover:text-[#E5C558] transition-colors">
                        Happy Client #{testimonial}
                      </div>
                      <div className="text-gray-600">Verified Customer</div>
                    </div>
                  </div>

                  {/* Quote Icon */}
                  <div className="absolute bottom-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <svg
                      className="w-8 h-8 text-[#E5C558]"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section
          className="py-20 lg:py-28 bg-gradient-to-r from-[#0b1220] via-[#1a2332] to-[#0b1220] relative overflow-hidden"
          data-section="cta"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              <defs>
                <pattern
                  id="grid"
                  width="10"
                  height="10"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 10 0 L 0 0 0 10"
                    fill="none"
                    stroke="#E5C558"
                    strokeWidth="0.5"
                  />
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#grid)" />
            </svg>
          </div>

          <div className="container mx-auto px-4 lg:px-6 text-center relative z-10">
            <div className="max-w-4xl mx-auto space-y-8">
              <h2 className="text-3xl lg:text-5xl font-bold text-white leading-tight">
                Ready to Transform Your Business with
                <span className="block text-[#E5C558] mt-2">
                  {service.title}?
                </span>
              </h2>

              <p className="text-xl lg:text-2xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
                Let's discuss how our expert {service.title.toLowerCase()}{" "}
                services can drive your business forward. Get a free
                consultation and personalized strategy.
              </p>

              <div className="flex flex-col sm:flex-row gap-6 justify-center pt-8">
                <Link
                  to="/contact-us"
                  className="group inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-[#0b1220] font-bold text-lg rounded-full hover:shadow-2xl hover:shadow-[#E5C558]/50 transition-all duration-300 hover:scale-105 focus:ring-4 focus:ring-[#E5C558]/30"
                  onClick={() =>
                    trackEvent(
                      "CTA",
                      `BottomContact_${service.url}`,
                      "ServicePage"
                    )
                  }
                  aria-label={`Contact us for ${service.title} services`}
                >
                  Get Free Consultation
                  <svg
                    className="w-6 h-6 ml-3 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </Link>

                <Link
                  to="/services"
                  className="group inline-flex items-center justify-center px-10 py-5 bg-transparent border-2 border-[#E5C558] text-[#E5C558] font-bold text-lg rounded-full hover:bg-[#E5C558] hover:text-[#0b1220] transition-all duration-300 focus:ring-4 focus:ring-[#E5C558]/30"
                  aria-label="Explore all our services"
                >
                  Explore All Services
                  <svg
                    className="w-6 h-6 ml-3 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                  </svg>
                </Link>
              </div>

              {/* Contact Info */}
              <div className="pt-12 border-t border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-gray-300">
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-4 bg-[#E5C558]/20 rounded-full flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-[#E5C558]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div className="text-sm text-gray-400 mb-1">Email Us</div>
                    <div className="font-semibold">support@geniusnexa.com</div>
                  </div>

                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-4 bg-[#E5C558]/20 rounded-full flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-[#E5C558]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                    </div>
                    <div className="text-sm text-gray-400 mb-1">Call Us</div>
                    <div className="font-semibold">+91 9944544603</div>
                  </div>

                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-4 bg-[#E5C558]/20 rounded-full flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-[#E5C558]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div className="text-sm text-gray-400 mb-1">
                      Support Hours
                    </div>
                    <div className="font-semibold">24/7 Available</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Services;
